<template>
  <footer class="organization-footer">
    <!-- Organization Footer Section -->
    <div class="organization-section">
      <v-container>
        <v-row>
          <v-col cols="12" :md="footerColumnSize">
            <div class="footer-section">
              <h3 class="footer-title organization-title">
                <router-link 
                  :to="getLocalisedOrganizationUrl(organization)"
                  class="organization-title-link"
                >
                  {{ organization.name }}
                </router-link>
              </h3>
              <div v-if="socialMediaLinks.length > 0" class="footer-links">
                <div
                  v-for="link in socialMediaLinks"
                  :key="link.type"
                  class="footer-link"
                  @click="openSocialLink(link.url)"
                  :title="getPlatformDisplayName(link.type)"
                >
                  <v-icon color="white" size="24">
                    {{ getPlatformIcon(link.type) }}
                  </v-icon>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" :md="footerColumnSize" v-if="organizationClasses.length > 0">
            <div class="footer-section">
              <div class="mb-5">
                <h3 class="footer-title footer-subtitle">
                  <router-link 
                    :to="getLocalisedOrganizationUrl(organization)"
                    class="footer-subtitle-link"
                  >
                    {{ $t('layout.organization.footer.classes-title') }}
                  </router-link>
                </h3>
              </div>
              <div class="footer-classes-list">
                <div
                  v-for="classItem in organizationClasses.slice(0, 5)"
                  :key="classItem.id"
                  class="footer-class-item"
                >
                  <router-link 
                    :to="getLocalisedClassUrl(classItem)"
                    class="footer-class-link"
                  >
                    {{ classItem.name }}
                  </router-link>
                </div>
                <div v-if="organizationClasses.length > 5" class="footer-class-more">
                  <router-link 
                    :to="getLocalisedOrganizationUrl(organization)"
                    class="footer-class-link"
                  >
                    {{ $t('layout.organization.footer.view-all-classes') }}
                  </router-link>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" :md="footerColumnSize" v-if="hasContactInfo">
            <div class="footer-section">
              <div class="mb-5">
                <h3 class="footer-title footer-subtitle">
                  {{ $t('layout.organization.footer.contact-title') }}
                </h3>
              </div>
              <!--
                The contacts page, directly under the heading it belongs to. Not an icon in the row below:
                those three act — write, call, open a map — and navigating among them looked like a
                fourth way to get in touch. A word says what it is.
              -->
              <router-link
                v-if="showStudioContacts"
                :to="studioContactsUrl"
                class="footer-class-link footer-contacts-link"
              >{{ $t('pages.studio-contacts.link') }}</router-link>
              <div class="footer-links">
                <div
                  v-if="organization.contact_email"
                  class="footer-link"
                  @click="openEmail(organization.contact_email)"
                  :title="$t('layout.organization.footer.contact-email')"
                >
                  <v-icon color="white" size="24">mdi-email</v-icon>
                </div>
                <div
                  v-if="organization.contact_phone"
                  class="footer-link"
                  @click="openPhone(organization.contact_phone)"
                  :title="$t('layout.organization.footer.contact-phone')"
                >
                  <v-icon color="white" size="24">mdi-phone</v-icon>
                </div>
                <div
                  v-if="organization.address"
                  class="footer-link"
                  @click="openAddress(organization.address)"
                  :title="$t('layout.organization.footer.contact-address')"
                >
                  <v-icon color="white" size="24">mdi-map-marker</v-icon>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- ASport Footer Section -->
    <div class="asport-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" class="text-center text-md-left">
            <p class="copyright mb-0">
              © {{ currentYear }} <a :href="getLocalisedUrl('/')" target="_blank" class="asport-link">ASport.lv</a>. Visas tiesības aizsargātas<span class="separator">•</span><router-link to="/terms" class="asport-link">{{ $t('layout.footer.links.terms') }}</router-link>
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <div class="footer-meta text-center text-md-right">
              <span class="powered-by">
                <a :href="getLocalisedUrl('/')" target="_blank" class="asport-link">{{ $t('layout.footer.powered-by') }}</a>
              </span>
              <span class="separator">•</span>
              <span>{{ $t('layout.footer.meta.made-in-latvia') }}</span>
              <v-icon small class="ml-1" color="#FF0000">mdi-heart</v-icon>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </footer>
</template>

<script>
import { OrganizationPath } from '@/router/organizationPaths'
import { mapGetters } from 'vuex'
import { parseMediaLinksFromApi, getPlatformDisplayName, getPlatformIcon } from '@/js/utils/socialMediaUtil'
import urlUtil from '@/js/utils/urlUtil'

export default {
  name: 'OrganizationFooter',
  computed: {
    /**
     * Only when the studio publishes its contact details. Otherwise the page has nothing on it but
     * an e-mail the footer already shows, and linking to it advertises a page the studio chose not
     * to have.
     */
    showStudioContacts () {
      return this.organization?.company_details_public === true
    },
    /** The studio's legal-details page. Path built from the router constant, never a literal. */
    studioContactsUrl () {
      return this.getLocalisedOrganizationUrl(this.organization, OrganizationPath.CONTACTS)
    },
    ...mapGetters('organization', ['organization']),
    currentYear() {
      return new Date().getFullYear()
    },
    socialMediaLinks() {
      if (!this.organization?.social_media_settings) return []
      return parseMediaLinksFromApi(this.organization.social_media_settings)
    },
    hasContactInfo() {
      return this.organization?.contact_email || this.organization?.contact_phone || this.organization?.address
    },
    organizationClasses() {
      return this.$store.getters['organization/classes'] || []
    },
    footerColumnSize() {
      let sectionCount = 1
      if (this.organizationClasses.length > 0) sectionCount++
      if (this.hasContactInfo) sectionCount++
      
      // If we have 3 sections (name + classes + contacts), use md="4" each
      // If we have 2 sections, use md="6" each
      return sectionCount % 3 === 0 ? '4' : '6'
    }
  },
  methods: {
    ...urlUtil,
    
    getPlatformDisplayName(platform) {
      return getPlatformDisplayName(platform)
    },
    getPlatformIcon(platform) {
      return getPlatformIcon(platform)
    },
    openSocialLink(url) {
      if (url) {
        window.open(urlUtil.formatUrl(url), '_blank')
      }
    },
    openEmail(email) {
      if (email) {
        window.location.href = `mailto:${email}`
      }
    },
    openPhone(phone) {
      if (phone) {
        window.location.href = `tel:${phone}`
      }
    },
    openAddress(address) {
      if (address) {
        const encodedAddress = encodeURIComponent(address)
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.organization-footer {
  margin-top: auto;
}
.organization-section {
  background: var(--organization-color-primary);
  padding: 40px 0;
}

.footer-section {
  text-align: left;
}

.footer-title {
  font-weight: 600;
  margin-bottom: 12px;
  position: relative;
}

/* Organization title - white and larger */
.organization-title {
  font-size: 24px;
  color: white;
}

/* Contact title - organization color, smaller, with underline */
.footer-subtitle {
  font-size: 16px;
  color: white;/*(var(--organization-color-secondary))*/
  padding-top: 4px;
}

.footer-subtitle::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0;
  width: 30px;
  height: 2px;
  background: white;/*(var(--organization-color-secondary))*/
}

.organization-title-link {
  color: white;
  text-decoration: none;
}

.organization-title-link:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

.footer-subtitle-link {
  color: white;
  text-decoration: none;
  display: block;
}

.footer-subtitle-link:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

.footer-classes-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-class-item {
  margin-bottom: 4px;
}

.footer-class-link {
  color: white;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.4;
}

.footer-class-link:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

.footer-class-more {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.footer-class-more .footer-class-link {
  font-weight: 600;
  font-style: italic;
}

.footer-links {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
}

.footer-link:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.footer-link .v-icon {
  color: white !important;
}

.asport-section {
  background: var(--gradient-header-footer);
  padding: 0px;
}

.asport-section .copyright,
.asport-section .footer-meta {
  color: #95a5a6;
  font-size: 0.85rem;
}

.asport-section .asport-link {
  color: var(--color-pallet-1);
  text-decoration: none;
  font-weight: 600;
}

.asport-section .asport-link:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

.copyright {
  font-size: 14px;
}

@media (min-width: 960px) {
  .footer-meta {
    justify-content: flex-end;
  }
}

.separator {
  margin: 0 0.75rem;
  opacity: 0.5;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .footer-section {
    text-align: center;
    place-items: center;
  }
  .footer-section .footer-subtitle {
    margin-bottom: 16px;
    font-size: 18px;
  }
  
  .footer-links {
    justify-content: center;
  }
}

/* The rekvizīti link sits under the contact icons, separated enough to read as its own entry. */
.footer-contacts-link {
  display: inline-block;
  margin-bottom: 16px;
}

</style>
